import React from 'react';

import { useGetGroups as useGetArtemyGroups } from '@vlabs/api/artemy/client';
import type { PermissionTag as PermissionTagName } from '@vlabs/api/artemy/model';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import { routes } from '@vlapps/point/src/app/routes';
import classNames from 'classnames';
import { generatePath } from 'react-router-dom';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { FoldableTag } from '@vlabs/monokit/shared/ui/foldable-tag';
import { Link } from '@vlabs/monokit/shared/ui/link';

import st from './DepartmentPermissionTagInfo.module.sass';
import { useDepartmentPermissionTagInfoTranslation } from './i18n';
import { PermissionTag } from '../permission-tag';

export interface DepartmentPermissionTagInfoProps extends WithClassNameProp, WithDataTestid {
  actions?: React.ReactNode;
  permissionTag: PermissionTagName;
  isOpen?: boolean;
}

const displayName = 'DepartmentPermissionTagInfo';
const testid = buildTestid(displayName);

function DepartmentPermissionTagInfo({
  actions = undefined,
  className = undefined,
  permissionTag,
  isOpen = false,
  'data-testid': dataTestId = undefined,
}: DepartmentPermissionTagInfoProps): React.JSX.Element {
  const { t } = useDepartmentPermissionTagInfoTranslation();

  const getGroupsDetails = useGetArtemyGroups({
    permission_tags: [permissionTag],
  });

  const groupsDetails = getGroupsDetails.data?.groups ?? [];

  return (
    <FoldableTag
      actions={actions}
      className={classNames(st.Root, className)}
      heading={(
        <PermissionTag
          className={st.PermissionTag}
          permissionTag={permissionTag}
          {...testid('PermissionTag', dataTestId)}
        />
      )}
      isOpen={isOpen}
      {...testid(dataTestId)}
    >
      {groupsDetails.length !== 0 && (
        <div
          className={st.Groups}
          {...testid('Groups', dataTestId)}
        >
          <div className={st.Subtitle}>{t('groups.title')}</div>

          {groupsDetails.map((group) => {
            return (
              <Link
                key={group.group_id}
                size="medium"
                to={generatePath(routes.sources.groupView, { groupId: group.group_id })}
                type="secondary"
              >
                {group.group_name ?? group.group_id}
              </Link>
            );
          })}
        </div>
      )}
    </FoldableTag>
  );
}

DepartmentPermissionTagInfo.displayName = displayName;

export { DepartmentPermissionTagInfo };
